/**
 * iter834 — One-tap "Void last order" flow.
 *
 * Rendered as a modal-inside-modal from OrderCompleteDialog. Requires a
 * manager/admin PIN (verified via /api/cashier/kiosk/verify-manager-pin —
 * same endpoint the Kiosk-Lock unlock screen uses) so cashiers can't
 * self-authorize voids. Reason field is required; both PIN + reason go
 * to the audit trail via the DELETE /api/cashier/orders/{id} endpoint.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ShieldAlert, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function VoidLastOrderDialog({ open, onOpenChange, order, onVoided }) {
  const [pin, setPin] = useState('');
  const [reason, setReason] = useState('Cashier mis-tap');
  const [busy, setBusy] = useState(false);
  const [managerName, setManagerName] = useState('');

  useEffect(() => {
    if (!open) { setPin(''); setReason('Cashier mis-tap'); setBusy(false); setManagerName(''); }
  }, [open]);

  const submit = async () => {
    if (!pin || pin.length < 4) { toast.error('Enter a manager/admin PIN'); return; }
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    if (!order?.id) { toast.error('No order to void'); return; }
    setBusy(true);
    try {
      // 1) Verify PIN (throws 403 if not a manager/admin)
      // iter845o — branch_id required so PIN check is tenant-scoped.
      let branch_id = order?.branch_id || null;
      if (!branch_id) {
        try {
          const raw = localStorage.getItem('cashier_user') || localStorage.getItem('user');
          if (raw) branch_id = JSON.parse(raw)?.branch_id || null;
        } catch { /* ignore */ }
      }
      const verify = await api.post('/cashier/kiosk/verify-manager-pin', { pin, branch_id });
      setManagerName(verify?.data?.name || '');
      // 2) Void the order (retains ZATCA row, reverses BOM, flips status to 'voided')
      const token = localStorage.getItem('cashier_token');
      await api.delete(`/cashier/orders/${order.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { reason: reason.trim(), authorized_by: verify?.data?.name || '', manager_pin: pin },
      });
      toast.success(`Order #${order.order_number} voided by ${verify?.data?.name || 'manager'}`);
      onVoided?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to void order');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-md max-h-[90dvh] overflow-y-auto" data-testid="void-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-error">
            <ShieldAlert size={22} /> Void Order #{order?.order_number}
          </DialogTitle>
        </DialogHeader>
        <div className="rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 p-3 text-xs text-red-900 dark:text-red-200">
          The order is retained for ZATCA audit (Article 75) but flagged <b>voided</b>.
          Any BOM ingredients are returned to stock. Requires a manager or admin PIN.
        </div>

        <div className="space-y-2">
          <Label className="text-xs">Manager / Admin PIN</Label>
          <Input
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            placeholder="••••"
            className="h-11 text-center text-lg tracking-widest"
            maxLength={8}
            data-testid="void-order-pin"
            autoFocus
          />
        </div>

        <div className="space-y-2">
          <Label className="text-xs">Reason (audit trail)</Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="e.g. Wrong item added, customer changed mind"
            data-testid="void-order-reason"
          />
        </div>

        <div className="flex gap-2 pt-1">
          <Button
            variant="outline"
            className="flex-1"
            disabled={busy}
            onClick={() => onOpenChange(false)}
            data-testid="void-order-cancel"
          >
            <X size={16} className="mr-1" /> Cancel
          </Button>
          <Button
            className="flex-[2] bg-error hover:bg-error/90 text-white"
            disabled={busy || !pin || !reason.trim()}
            onClick={submit}
            data-testid="void-order-confirm"
          >
            <Trash2 size={16} className="mr-2" />
            {busy ? 'Voiding…' : 'Void order'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
